/**
 * Cookie Consent Management
 *
 * GDPR-compliant consent tracking for analytics and functional cookies.
 * Consent state is stored in localStorage and checked before initializing
 * any non-essential tracking (PostHog, Langfuse).
 *
 * Cookie categories:
 * - essential: Always allowed. Firebase Auth tokens, workspace context.
 * - analytics: PostHog product analytics, session recording, heatmaps.
 * - functional: Langfuse LLM observability (if client-side tracing enabled).
 *
 * Two ways to reach a state (`basis`):
 * - explicit: the person chose on the banner or in Settings. Always wins.
 * - region_default: no choice was made and the `analytics-region-default` flag
 *   said this browser is outside the opt-in jurisdictions, so analytics is on
 *   by default. It is withdrawn (back to no decision, banner shown) whenever
 *   that flag later evaluates false for the browser, and replaced by any
 *   explicit choice. See `reconcileRegionDefault` in `regionConsentDefault.ts`.
 *
 * @compliance GDPR Article 7 — Conditions for consent
 * @compliance ePrivacy Directive Article 5(3) — Cookie consent
 */

const CONSENT_STORAGE_KEY = 'bower_cookie_consent';
const CONSENT_VERSION = 1;

export type ConsentBasis = 'explicit' | 'region_default';

export interface CookieConsentState {
  /** Consent schema version — bump when categories change to re-prompt */
  version: number;
  /** Essential cookies (auth, workspace) — always true, not user-configurable */
  essential: true;
  /** Analytics cookies (PostHog) */
  analytics: boolean;
  /** Functional cookies (Langfuse, future integrations) */
  functional: boolean;
  /** ISO 8601 timestamp of when consent was given/updated */
  consentedAt: string;
  /**
   * How this state was reached. Absent on every blob written before region
   * defaults existed, all of which were explicit choices, so absent reads as
   * explicit. Optional for that reason rather than a version bump, which would
   * re-prompt everyone who already chose.
   */
  basis?: ConsentBasis;
}

/** Read the current consent state from localStorage. Returns null if not yet given. */
export function getConsentState(): CookieConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CookieConsentState;

    // If the consent version is outdated, treat as no consent (re-prompt)
    if (parsed.version !== CONSENT_VERSION) return null;

    return parsed;
  } catch {
    return null;
  }
}

/** Save consent state to localStorage. */
export function saveConsentState(
  state: Omit<CookieConsentState, 'version' | 'essential' | 'consentedAt' | 'basis'>,
  basis: ConsentBasis = 'explicit',
): CookieConsentState {
  const fullState: CookieConsentState = {
    version: CONSENT_VERSION,
    essential: true,
    analytics: state.analytics,
    functional: state.functional,
    consentedAt: new Date().toISOString(),
    basis,
  };

  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(fullState));
  return fullState;
}

/** Accept all cookie categories. */
export function acceptAllCookies(): CookieConsentState {
  return saveConsentState({ analytics: true, functional: true });
}

/** Accept only essential cookies (reject analytics and functional). */
export function acceptEssentialOnly(): CookieConsentState {
  return saveConsentState({ analytics: false, functional: false });
}

/** Check if analytics cookies are consented. */
export function hasAnalyticsConsent(): boolean {
  const state = getConsentState();
  return state?.analytics === true;
}

/** Check if consent has been given at all (any decision made). */
export function hasConsentDecision(): boolean {
  return getConsentState() !== null;
}

/** True when the current state is the region default, not a choice. */
export function isRegionDefaultConsent(): boolean {
  return getConsentState()?.basis === 'region_default';
}

const consentChangeListeners = new Set<() => void>();

/**
 * Subscribe to region-default changes, which happen outside any component (on
 * the PostHog /flags response). Explicit choices are made BY the component that
 * renders them, so they do not notify. Returns the unsubscribe function.
 */
export function onRegionDefaultChange(listener: () => void): () => void {
  consentChangeListeners.add(listener);
  return () => {
    consentChangeListeners.delete(listener);
  };
}

function notifyRegionDefaultChange(): void {
  consentChangeListeners.forEach((listener) => listener());
}

/**
 * Turn analytics on by default for a browser that has made no choice.
 *
 * `beforeNotify` runs after the state is stored and before listeners hear of
 * it. The caller upgrades capture there: AFTER the write, so a write that
 * throws (storage blocked or full) leaves capture off, and BEFORE the banner is
 * notified, because the banner replays stashed payloads at once.
 */
export function applyRegionDefaultConsent(beforeNotify?: () => void): CookieConsentState {
  const state = saveConsentState({ analytics: true, functional: true }, 'region_default');
  beforeNotify?.();
  notifyRegionDefaultChange();
  return state;
}

/**
 * Withdraw a region default, returning the browser to "no decision" so the
 * banner shows again. A no-op (returns false) for an explicit choice, which a
 * region change must never overwrite.
 */
export function clearRegionDefaultConsent(): boolean {
  if (!isRegionDefaultConsent()) return false;
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  notifyRegionDefaultChange();
  return true;
}

/**
 * Current consent version. Bump this when cookie categories change
 * to force re-consent from all users.
 */
export { CONSENT_VERSION };
